import { useQuery } from '@tanstack/react-query'
import { usuarioService, type UserFilters } from '../../../services/usuario'
import { api } from '../../../services/axios'
import { authService } from '../../../services/auth'
import { apiService } from '../../demands/services/api'
import type { Setor } from '../../../models'

/**
 * Query keys para usuários
 */
export const userQueryKeys = {
  all: ['users'] as const,
  lists: () => [...userQueryKeys.all, 'list'] as const,
  list: (filters?: UserFilters) => [...userQueryKeys.lists(), filters] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...userQueryKeys.details(), id] as const,
}

/**
 * Query keys para setores
 */
export const setorQueryKeys = {
  all: ['setores'] as const,
  lists: () => [...setorQueryKeys.all, 'list'] as const,
  list: (filters?: string) => [...setorQueryKeys.lists(), { filters }] as const,
}

/**
 * Hook para buscar todos os usuários com filtros
 * @param filters - Filtros para busca
 * @param enabled - Se a query deve ser executada (default: true)
 */
export const useUsers = (filters?: UserFilters, enabled = true) => {
  return useQuery({
    queryKey: userQueryKeys.list(filters),
    queryFn: () => usuarioService.getAll(filters),
    enabled: enabled,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}

/**
 * Hook para buscar usuários dos setores do usuário logado (para não-admins)
 * @param enabled - Se a query deve ser executada (default: true)
 */
export const useUsersFromMySetores = (enabled = true) => {
  return useQuery({
    queryKey: [...userQueryKeys.lists(), 'my-setores'],
    queryFn: () => apiService.getUsuariosDosSetoresDoUsuarioLogado(),
    enabled: enabled,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}

/**
 * Hook para buscar um usuário específico
 */
export const useUser = (id: number) => {
  return useQuery({
    queryKey: userQueryKeys.detail(id),
    queryFn: () => usuarioService.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}

/**
 * Hook para buscar todos os setores
 * @param enabled - Se a query deve ser executada (default: true)
 */
export const useSetores = (enabled = true) => {
  return useQuery({
    queryKey: setorQueryKeys.lists(),
    queryFn: async () => {
      await authService.ensureSanctumInitialized()
      const response = await api.get<Setor[]>('/setores')
      return response.data
    },
    enabled: enabled,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}

/**
 * Hook para buscar apenas setores do usuário logado
 * @param enabled - Se a query deve ser executada (default: true)
 */
export const useMeusSetores = (enabled = true) => {
  return useQuery({
    queryKey: [...setorQueryKeys.lists(), 'meus'],
    queryFn: () => apiService.getMeusSetores(),
    enabled: enabled,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })
}
